import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import http from "services/httpClient";

import type {
  DeviceLabelItem,
  DeviceListItem,
  ReportParamUpsertItem,
  ReportTemplateParam,
} from "./types";

type Props = {
  show: boolean;
  onHide: () => void;
  clientId: number;
  templateId: number | null;
  templateName?: string;
  onSaved?: () => void;
};

export function ReportParamsModal({
    show,
    onHide,
    clientId,
    templateId,
    templateName,
    onSaved,
}: Props) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const [devices, setDevices] = useState<DeviceListItem[]>([]);
    const [labelsByDevice, setLabelsByDevice] = useState<Record<number, ReportLabelItem[]>>({});
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set()); // `${deviceId}:${col}`

    const selectedCount = selectedKeys.size;

    const keyOf = (deviceId: number, column_index: number) => `${deviceId}:${column_index}`;

    const DEVICES_ENDPOINT = (clientId: number) => `/clients/${clientId}/devices`;
    const LABELS_ENDPOINT = (deviceId: number) => `/device/${deviceId}/labels`;
    const CALC_ENDPOINT = (deviceId: number) => `/calculated_params/${deviceId}`;
    const STATS_ENDPOINT = (deviceId: number) => `/stat-definitions/device/${deviceId}`;

    
    

    type CalculatedParam = {
      result_label: string;
      column_index: number;
      unit?: string;
      expression?: string;
      };

  type LabelKind = "raw" | "calc" | "stat";

  type ReportLabelItem = DeviceLabelItem & {
    kind?: LabelKind;
  };

  type StatDefinition = {
    id: number;
    source_column_index: number;
    aggregation: "min" | "max" | "avg";
    window_hours: 1 | 6 | 8 | 12 | 24;
    result_label: string;
    unit?: string | null;
  };

  const load = async () => {
    if (!templateId) return;
    setLoading(true);
    setErr(null);

    try {
      // 1) devices
      const devRes = await http.get(DEVICES_ENDPOINT(clientId));
      const devs: DeviceListItem[] = devRes.data || [];
      setDevices(devs);

      // 2) current template params
      const paramsRes = await http.get(`/reports/templates/${templateId}/params`);
      const params: ReportTemplateParam[] = paramsRes.data || [];

      // set selections from current params
      const next = new Set<string>();
      params.forEach((p) => next.add(keyOf(p.device_id, p.column_index)));
      setSelectedKeys(next);

        // 3) load labels for each device
        const labelMap: Record<number, ReportLabelItem[]> = {};

        await Promise.all(
          devs.map(async (d) => {
            try {
              const [labRes, calcRes, statsRes] = await Promise.all([
                http.get(LABELS_ENDPOINT(d.id)),
                http.get(CALC_ENDPOINT(d.id)),
                http.get(STATS_ENDPOINT(d.id)),
              ]);

              const rawLabels: ReportLabelItem[] = ((labRes.data || []) as DeviceLabelItem[])
                .filter((l: any) => l.is_enabled !== false)
                .map((l) => ({
                  ...l,
                  kind: "raw",
                }));

              const calcParams = (calcRes.data || []) as CalculatedParam[];
              const calcLabels: ReportLabelItem[] = calcParams.map((c) => ({
                label: c.result_label,
                column_index: c.column_index,
                unit: c.unit || "",
                kind: "calc",
              }));

              const statDefs = (statsRes.data || []) as StatDefinition[];

              // give stats virtual indexes so selection keys stay unique
              // raw uses real x0..x99, calc uses x100+, stats are separate and should not collide
              const statLabels: ReportLabelItem[] = statDefs.map((s) => ({
                label: s.result_label,
                column_index: 1000 + s.id,
                unit: s.unit || "",
                kind: "stat",
              }));

              const merged = [...rawLabels, ...calcLabels, ...statLabels].sort(
                (a, b) => a.column_index - b.column_index
              );

              labelMap[d.id] = merged;
            } catch {
              try {
                const labRes = await http.get(LABELS_ENDPOINT(d.id));
                labelMap[d.id] = ((labRes.data || []) as DeviceLabelItem[])
                  .filter((l: any) => l.is_enabled !== false)
                  .map((l) => ({
                    ...l,
                    kind: "raw",
                  }));
              } catch {
                labelMap[d.id] = [];
              }
            }
          })
        );

        setLabelsByDevice(labelMap);
      setLabelsByDevice(labelMap);
    } catch (e: any) {
      setErr("Failed to load parameters. Check devices/labels endpoints.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, templateId]);

  const toggle = (deviceId: number, col: number) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const k = keyOf(deviceId, col);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const selectAllDevice = (deviceId: number) => {
    const labels = labelsByDevice[deviceId] || [];
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      labels.forEach((l) => next.add(keyOf(deviceId, l.column_index)));
      return next;
    });
  };

  const clearAllDevice = (deviceId: number) => {
    const labels = labelsByDevice[deviceId] || [];
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      labels.forEach((l) => next.delete(keyOf(deviceId, l.column_index)));
      return next;
    });
  };

  const save = async () => {
    if (!templateId) return;

    setSaving(true);
    setErr(null);

    try {
      // convert Set keys to items payload
      const items: ReportParamUpsertItem[] = [];

      // label snapshots (nice for stability even if label changes later)
      const labelLookup = new Map<string, string>();
      Object.entries(labelsByDevice).forEach(([devIdStr, labels]) => {
        const devId = Number(devIdStr);
        labels.forEach((l) => labelLookup.set(keyOf(devId, l.column_index), l.label));
      });

      selectedKeys.forEach((k) => {
        const [dStr, cStr] = k.split(":");
        const device_id = Number(dStr);
        const column_index = Number(cStr);
        items.push({
          device_id,
          column_index,
          label_snapshot: labelLookup.get(k) ?? null,
        });
      });

      await http.put(`/reports/templates/${templateId}/params`, { items });

      onHide();
      onSaved?.();
    } catch (e: any) {
      setErr("Failed to save parameters.");
    } finally {
      setSaving(false);
    }
  };

  const rows = useMemo(() => {
    return devices.map((d) => {
      const labels = labelsByDevice[d.id] || [];
      return { device: d, labels };
    });
  }, [devices, labelsByDevice]);

  return (
    <Modal
        show={show}
        onHide={onHide}
        centered
        size="xl"
        scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Parameters{templateName ? ` — ${templateName}` : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {err && <Alert variant="danger">{err}</Alert>}

        {loading ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <div>Loading…</div>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="text-muted">
                Selected: <b>{selectedCount}</b>
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Tip: select per device, then Save.
              </div>
            </div>

            <Table responsive hover size="sm" className="align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 220 }}>Device</th>
                  <th>Labels</th>
                </tr>
              </thead>

              <tbody>
                {rows.map(({ device, labels }) => (
                  <tr key={device.id}>
                    <td>
                      <div className="fw-semibold">{device.device_name}</div>
                      <div className="d-flex gap-2 mt-2">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => selectAllDevice(device.id)}
                          disabled={!labels.length}
                        >
                          Select all
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => clearAllDevice(device.id)}
                          disabled={!labels.length}
                        >
                          Clear
                        </Button>
                      </div>
                    </td>

                    <td>
                      {labels.length === 0 ? (
                        <div className="text-muted">No labels found.</div>
                      ) : (
                        <div
                          className="d-flex flex-wrap"
                          style={{ gap: 12, maxHeight: 180, overflowY: "auto" }}
                        >
                          {labels.map((l) => {
                            const checked = selectedKeys.has(keyOf(device.id, l.column_index));
                            return (
                              <Form.Check
                                key={l.column_index}
                                type="checkbox"
                                id={`p-${device.id}-${l.column_index}`}
                                label={
                                  <span>
                                  {l.label}

                                  {l.kind === "calc" && (
                                    <span
                                      className="badge ms-2"
                                      style={{
                                        backgroundColor: "#cfe8ff",
                                        color: "#0c5460",
                                        fontWeight: 500,
                                      }}
                                    >
                                      calc
                                    </span>
                                  )}

                                  {l.kind === "stat" && (
                                    <span
                                      className="badge ms-2"
                                      style={{
                                        backgroundColor: "#ffe5b4",
                                        color: "#7a4b00",
                                        fontWeight: 500,
                                      }}
                                    >
                                      stat
                                    </span>
                                  )}
                                </span>
                                }
                                checked={checked}
                                onChange={() => toggle(device.id, l.column_index)}
                              />
                            );
                          })}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}

                {devices.length === 0 && (
                  <tr>
                    <td colSpan={2} className="text-muted">
                      No devices found for this client.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={save} disabled={saving || loading}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ReportParamsModal;